import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { apiClient, type ShopProduct } from '../services/apiClient';
import { CART_STORAGE_KEY, CART_STORAGE_VERSION, MAX_QTY, MIN_QTY } from '../config/shop';
import ReceiptStampToast, { type ReceiptNotice } from '../components/shop/ReceiptStampToast';
import { formatMoney } from '../utils/money';

interface StoredCartItem { productId: string; quantity: number }
interface StoredCart {
  v: 1;
  items: StoredCartItem[];
  attemptId: string | null;
  snapshot: Record<string, ShopProduct>;
}

export interface CartLine {
  productId: string;
  quantity: number;
  product: ShopProduct | null;
  unavailable: boolean;
  priceChanged: boolean;
}

type CatalogState = 'idle' | 'loading' | 'ready' | 'error';

interface CartContextValue {
  lines: CartLine[];
  itemCount: number;
  subtotalCents: number;
  currency: string;
  attemptId: string | null;
  catalogState: CatalogState;
  catalogError: string | null;
  canCheckout: boolean;
  addItem: (product: ShopProduct, quantity?: number) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => CartLine | null;
  restoreItem: (line: CartLine) => void;
  clearCart: () => void;
  reconcile: () => Promise<void>;
  ensureAttemptId: () => string;
  regenerateAttemptId: () => string;
  acceptQuote: (totalCents: number, currency: string) => string;
}

const CartContext = createContext<CartContextValue | null>(null);

function newAttemptId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function emptyCart(): StoredCart {
  return { v: CART_STORAGE_VERSION, items: [], attemptId: null, snapshot: {} };
}

function readStoredCart(): StoredCart {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return emptyCart();
    const parsed = JSON.parse(raw) as Partial<StoredCart>;
    if (parsed.v !== CART_STORAGE_VERSION || !Array.isArray(parsed.items) || typeof parsed.snapshot !== 'object' || !parsed.snapshot) {
      return emptyCart();
    }
    const items = parsed.items
      .filter((item): item is StoredCartItem => !!item && typeof item.productId === 'string' && Number.isInteger(item.quantity))
      .map((item) => ({ productId: item.productId, quantity: Math.min(MAX_QTY, Math.max(MIN_QTY, item.quantity)) }));
    return { v: 1, items, attemptId: typeof parsed.attemptId === 'string' ? parsed.attemptId : null, snapshot: parsed.snapshot as Record<string, ShopProduct> };
  } catch {
    return emptyCart();
  }
}

export const CartProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [cart, setCart] = useState<StoredCart>(readStoredCart);
  const cartRef = useRef(cart);
  const [unavailable, setUnavailable] = useState<Set<string>>(new Set());
  const [priceChanged, setPriceChanged] = useState<Set<string>>(new Set());
  const [catalogState, setCatalogState] = useState<CatalogState>('idle');
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [notice, setNotice] = useState<ReceiptNotice | null>(null);
  const [quoteSignature, setQuoteSignature] = useState<string | null>(null);

  const persist = useCallback((next: StoredCart) => {
    cartRef.current = next;
    setCart(next);
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(next));
  }, []);

  const lines = useMemo<CartLine[]>(() => cart.items.map((item) => ({
    ...item,
    product: cart.snapshot[item.productId] ?? null,
    unavailable: unavailable.has(item.productId) || !cart.snapshot[item.productId],
    priceChanged: priceChanged.has(item.productId),
  })), [cart, unavailable, priceChanged]);

  const itemCount = useMemo(() => cart.items.reduce((sum, item) => sum + item.quantity, 0), [cart.items]);
  const subtotalCents = useMemo(() => lines.reduce((sum, line) => sum + (line.product?.retailPriceCents ?? 0) * line.quantity, 0), [lines]);
  const currencies = useMemo(() => new Set(lines.flatMap((line) => line.product ? [line.product.currency.toLowerCase()] : [])), [lines]);
  const currency = currencies.values().next().value ?? 'usd';
  const canCheckout = lines.length > 0 && catalogState === 'ready' && lines.every((line) => !line.unavailable) && currencies.size === 1;

  const resetAttempt = useCallback((next: StoredCart): StoredCart => ({
    ...next,
    attemptId: next.items.length ? newAttemptId() : null,
  }), []);

  const addItem = useCallback((product: ShopProduct, quantity = 1) => {
    const amount = Math.min(MAX_QTY, Math.max(MIN_QTY, quantity));
    const existing = cart.items.find((item) => item.productId === product.id);
    const nextQuantity = Math.min(MAX_QTY, (existing?.quantity ?? 0) + amount);
    const items = existing
      ? cart.items.map((item) => item.productId === product.id ? { ...item, quantity: nextQuantity } : item)
      : [...cart.items, { productId: product.id, quantity: amount }];
    const next = resetAttempt({ ...cart, items, snapshot: { ...cart.snapshot, [product.id]: product } });
    persist(next);
    setQuoteSignature(null);
    const nextSubtotal = items.reduce((sum, item) => sum + (next.snapshot[item.productId]?.retailPriceCents ?? 0) * item.quantity, 0);
    setNotice((current) => ({ id: (current?.id ?? 0) + 1, product, quantity: nextQuantity, subtotalCents: nextSubtotal }));
  }, [cart, persist, resetAttempt]);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    const bounded = Math.min(MAX_QTY, Math.max(MIN_QTY, quantity));
    persist(resetAttempt({ ...cart, items: cart.items.map((item) => item.productId === productId ? { ...item, quantity: bounded } : item) }));
    setQuoteSignature(null);
  }, [cart, persist, resetAttempt]);

  const removeItem = useCallback((productId: string) => {
    const line = lines.find((candidate) => candidate.productId === productId) ?? null;
    persist(resetAttempt({ ...cart, items: cart.items.filter((item) => item.productId !== productId) }));
    setQuoteSignature(null);
    return line;
  }, [cart, lines, persist, resetAttempt]);

  const restoreItem = useCallback((line: CartLine) => {
    if (!line.product) return;
    const items = [...cart.items.filter((item) => item.productId !== line.productId), { productId: line.productId, quantity: line.quantity }];
    persist(resetAttempt({ ...cart, items, snapshot: { ...cart.snapshot, [line.productId]: line.product } }));
  }, [cart, persist, resetAttempt]);

  const clearCart = useCallback(() => {
    persist(emptyCart());
    setUnavailable(new Set());
    setPriceChanged(new Set());
    setQuoteSignature(null);
  }, [persist]);

  const reconcile = useCallback(async () => {
    if (cart.items.length === 0) {
      setCatalogState('ready');
      setCatalogError(null);
      return;
    }
    setCatalogState('loading');
    setCatalogError(null);
    try {
      const products = await apiClient.getShopProducts();
      const currentCart = cartRef.current;
      const byId = new Map(products.map((product) => [product.id, product]));
      const missing = new Set<string>();
      const changed = new Set<string>();
      const snapshot = { ...currentCart.snapshot };
      for (const item of currentCart.items) {
        const current = byId.get(item.productId);
        if (!current) {
          missing.add(item.productId);
          continue;
        }
        const previous = snapshot[item.productId];
        if (previous && (previous.retailPriceCents !== current.retailPriceCents || previous.currency !== current.currency)) changed.add(item.productId);
        snapshot[item.productId] = current;
      }
      persist({ ...currentCart, snapshot });
      setUnavailable(missing);
      setPriceChanged(changed);
      setCatalogState('ready');
    } catch (error) {
      setCatalogState('error');
      setCatalogError(error instanceof Error ? error.message : 'The live catalog could not be checked.');
    }
  }, [cart, persist]);

  const ensureAttemptId = useCallback(() => {
    const currentCart = cartRef.current;
    if (currentCart.attemptId) return currentCart.attemptId;
    const attemptId = newAttemptId();
    persist({ ...currentCart, attemptId });
    return attemptId;
  }, [persist]);

  const regenerateAttemptId = useCallback(() => {
    const attemptId = newAttemptId();
    persist({ ...cartRef.current, attemptId });
    return attemptId;
  }, [persist]);

  const acceptQuote = useCallback((totalCents: number, nextCurrency: string) => {
    const signature = `${nextCurrency.toLowerCase()}:${totalCents}`;
    if (quoteSignature && quoteSignature !== signature) {
      const attemptId = newAttemptId();
      persist({ ...cartRef.current, attemptId });
      setQuoteSignature(signature);
      return attemptId;
    }
    setQuoteSignature(signature);
    return ensureAttemptId();
  }, [ensureAttemptId, persist, quoteSignature]);

  const value = useMemo<CartContextValue>(() => ({
    lines, itemCount, subtotalCents, currency, attemptId: cart.attemptId, catalogState, catalogError, canCheckout,
    addItem, updateQuantity, removeItem, restoreItem, clearCart, reconcile, ensureAttemptId, regenerateAttemptId, acceptQuote,
  }), [lines, itemCount, subtotalCents, currency, cart.attemptId, catalogState, catalogError, canCheckout,
    addItem, updateQuantity, removeItem, restoreItem, clearCart, reconcile, ensureAttemptId, regenerateAttemptId, acceptQuote]);

  return (
    <CartContext.Provider value={value}>
      {children}
      <div className="sr-only" role="status" aria-live="polite">
        {notice ? `${notice.quantity} ${notice.product.name} in cart. Running subtotal ${formatMoney(notice.subtotalCents, notice.product.currency)}.` : ''}
      </div>
      <ReceiptStampToast notice={notice} onDismiss={() => setNotice(null)} />
    </CartContext.Provider>
  );
};

export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) throw new Error('useCart must be used inside CartProvider');
  return value;
}

export function useOptionalCart(): CartContextValue | null {
  return useContext(CartContext);
}
